import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, effect, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormMode } from 'src/app/controls/common/form-mode';
import { RestApiEventConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-event-configuration';
import { ApiConfigurationState } from '../models/api-configuration-state';
import { ActionsFormComponent } from './actions-form/actions-form.component';
import { ConditionsFormComponent } from './conditions-form/conditions-form.component';

@Component({
    selector: 'app-api-configuration-event',
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, ConditionsFormComponent, ActionsFormComponent],
    templateUrl: './api-configuration-event.component.html',
    styleUrl: './api-configuration-event.component.scss'
})
export class ApiConfigurationEventComponent implements OnInit {
    /**
     * Etat de la configuration d'API
     */
    public readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public FormMode = FormMode;

    /**
     * évènement en cours d'edition.
     */
    @Input()
    editedEventConfiguration: RestApiEventConfiguration;

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    @Output()
    canceled: EventEmitter<void> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Sauvegarder'.
     */
    @Output()
    saved: EventEmitter<void> = new EventEmitter();

    /**
     * Formulaire contenant les informations relatives à un évènement de la configuration d'API.
     */
    public apiConfigurationEventForm: FormGroup;

    /**
     * Constructeur.
     */
    constructor() {
        this.apiConfigurationEventForm = new FormGroup({
            name: new FormControl(''),
            description: new FormControl(''),
            conditions: new UntypedFormControl([]),
            actions: new UntypedFormControl([]),
        });

        effect(() => {
            if (this.apiConfigurationState.$mode() === FormMode.Reading) {
                this.apiConfigurationEventForm.disable();
            } else {
                this.apiConfigurationEventForm.enable();
            }
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public onCancelClick() {
        this.canceled.emit();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    public onSaveClick() {
        let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();
        let eventConfiguration: RestApiEventConfiguration = null;

        if (this.editedEventConfiguration.id === null) {
            this.editedEventConfiguration.id = this.getNextId();
            this.editedEventConfiguration.order = this.getLastPosition();
            apiConfiguration.eventConfigurations.push(this.editedEventConfiguration);
        } else {
            this.editedEventConfiguration = apiConfiguration.eventConfigurations.find((x) => x.id === this.editedEventConfiguration.id);
        }

        this.editedEventConfiguration.name = this.apiConfigurationEventForm.get('name').value;
        this.editedEventConfiguration.description = this.apiConfigurationEventForm.get('description').value;
        this.editedEventConfiguration.conditions = this.apiConfigurationEventForm.get('conditions').value;
        this.editedEventConfiguration.actions = this.apiConfigurationEventForm.get('actions').value;

        this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
        this.saved.emit();
    }

    /**
     * Remplit les champs du formulaire avec les données de la configuration d'API.
     */
    private fillForm() {
        this.apiConfigurationEventForm.get('name').setValue(this.editedEventConfiguration.name);
        this.apiConfigurationEventForm.get('description').setValue(this.editedEventConfiguration.description);
        this.apiConfigurationEventForm.get('conditions').setValue(this.editedEventConfiguration?.conditions);
        this.apiConfigurationEventForm.get('actions').setValue(this.editedEventConfiguration?.actions);
    }

    /**
     * Retourne l'id autogénéré pour l'évènement.
     * @returns un Id.
     */
    private getNextId() {
        let nextId = 1;

        if (this.apiConfigurationState.$apiConfiguration().eventConfigurations.length !== 0) {
            nextId = this.apiConfigurationState.$apiConfiguration().eventConfigurations.reduce((ev1, ev2) => (ev1.id > ev2.id ? ev1 : ev2)).id + 1;
        }

        return nextId;
    }

    /**
     * Retourne la position du nouvel évènement.
     * @returns une position à la fin de la liste.
     */
    private getLastPosition() {
        let lastPosition = 1;

        if (this.apiConfigurationState.$apiConfiguration().eventConfigurations.length !== 0) {
            lastPosition = this.apiConfigurationState.$apiConfiguration().eventConfigurations.reduce((ev1, ev2) => (ev1.id > ev2.id ? ev1 : ev2)).order + 1;
        }

        return lastPosition;
    }
}
